import { useIsMutating, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";

import { generateUploadUrlControllerGenerateUploadUrl } from "@/generated/files/files";
import { GenerateUploadUrlDtoAssociatedEntityType } from "@/generated/model";
import {
  setEmployeePhoto,
  useRemoveEmployeePhoto,
} from "@/generated/people/people";
import { useInvalidateEmployeeProfile } from "@/hooks/use-invalidate-employee-profile";
import { extractApiErrorMessage } from "@/lib/compliance/invalidate-compliance-queries";

function employeePhotoMutationKey(userId: string) {
  return ["employee-photo", userId];
}

/** True while this person's photo is being saved or removed, from anywhere. */
export function useIsEmployeePhotoChanging(userId: string): boolean {
  return useIsMutating({ mutationKey: employeePhotoMutationKey(userId) }) > 0;
}

/**
 * Add, replace and remove one person's photo. The follow-up (refetch, toast)
 * is set here rather than per call, so it still runs if the menu closes
 * before the upload finishes.
 */
export function useEmployeePhoto(nurseryId: string, userId: string) {
  const invalidateProfile = useInvalidateEmployeeProfile(nurseryId, userId);

  // The image goes straight to storage through a signed URL bound to the
  // file's type and size, so storage rejects anything but the checked file.
  const upload = useMutation({
    mutationKey: employeePhotoMutationKey(userId),
    mutationFn: async (file: File) => {
      const response = await generateUploadUrlControllerGenerateUploadUrl({
        fileName: file.name,
        mimeType: file.type,
        size: file.size,
        associatedEntityType: GenerateUploadUrlDtoAssociatedEntityType.USER,
        associatedEntityId: userId,
      });
      const { fileId, uploadUrl } = response.data;
      if (!fileId || !uploadUrl) {
        throw new Error("Invalid response from generate upload URL");
      }

      const uploaded = await fetch(uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": file.type },
        body: file,
      });
      if (!uploaded.ok) {
        throw new Error("Upload failed");
      }

      await setEmployeePhoto(nurseryId, userId, { fileId });
    },
    onSuccess: async () => {
      await invalidateProfile();
      toast.success("Photo saved");
    },
    onError: (error) => {
      toast.error(extractApiErrorMessage(error, "Could not save the photo"));
    },
  });

  const remove = useRemoveEmployeePhoto({
    mutation: {
      mutationKey: employeePhotoMutationKey(userId),
      onSuccess: async () => {
        await invalidateProfile();
        toast.success("Photo removed");
      },
      onError: (error: unknown) => {
        toast.error(
          extractApiErrorMessage(error, "Could not remove the photo"),
        );
      },
    },
  });

  return { upload, remove };
}
